import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WidgetProps } from '@/lib/widgets/types';
import { Calendar, Clock, Shield, FileText, User, Wrench, AlertCircle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useUpcomingEvents } from '@/hooks/use-upcoming-events';
import { useAuth } from '@/hooks/use-auth';
import { useNavigate } from 'react-router-dom';

export function CalendarWidget({ isEditing = false }: WidgetProps) {
  const { activeCompany } = useAuth();
  const navigate = useNavigate();
  const { data: events = [], isLoading } = useUpcomingEvents(activeCompany?.id);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'certificate': return <Shield className="h-3 w-3" />;
      case 'document': return <FileText className="h-3 w-3" />;
      case 'driver_license': return <User className="h-3 w-3" />;
      case 'maintenance': return <Wrench className="h-3 w-3" />;
      case 'ticket': return <AlertCircle className="h-3 w-3" />;
      default: return <Calendar className="h-3 w-3" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'low': return 'bg-success/10 text-success border-success/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return 'Alto';
      case 'medium': return 'Médio';
      case 'low': return 'Baixo';
      default: return 'Normal';
    }
  };

  const handleEventClick = (event: any) => {
    if (isEditing) return;

    switch (event.source) {
      case 'noteone_certificates':
        navigate('/noteone/certificates');
        break;
      case 'fleet_documents':
        navigate('/fleet/documents');
        break;
      case 'fleet_drivers':
        navigate('/fleet/drivers');
        break;
      case 'fleet_maint_plans':
        navigate('/fleet/maintenance');
        break;
      case 'support_tickets':
        navigate('/company/support');
        break;
    }
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Próximos Eventos
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-32">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Carregando eventos...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Próximos Eventos
          {events.length > 0 && (
            <Badge variant="secondary" className="ml-auto text-xs">
              {events.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <Calendar className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Nenhum evento próximo</p>
            <p className="text-xs text-muted-foreground">Verifique novamente em breve</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.slice(0, 4).map((event) => (
              <div 
                key={event.id} 
                className={`border-l-2 border-primary pl-3 pb-2 ${!isEditing ? 'cursor-pointer hover:bg-accent/50 rounded-r-md pr-2 py-1 transition-colors' : ''}`}
                onClick={() => handleEventClick(event)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getEventIcon(event.type)}
                      <p className="text-sm font-medium truncate">{event.title}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {format(event.date, "dd 'de' MMM", { locale: ptBR })}
                      {event.time && (
                        <>
                          <Clock className="h-3 w-3 ml-1" />
                          {event.time}
                        </>
                      )}
                    </div>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`text-xs min-w-[60px] justify-center ${getPriorityColor(event.priority)}`}
                  >
                    {getPriorityLabel(event.priority)}
                  </Badge>
                </div>
              </div>
            ))}
            {events.length > 4 && (
              <div className="text-center pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  +{events.length - 4} eventos adicionais
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}